import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import sal from "sal.js";
import { useTranslation } from "react-i18next";

import { isRtlLanguage } from "@/lib/i18n";

import CallToActionOne from "../../my_components/CallToAction/CallToActionOne";
import BlogWidget from "../../my_components/Blogs/Widgets/BlogWidget";
import Testimonial_2 from "../Testimonials/Testimonial_2";
import Newsletter from "../../my_components/Newsletters/Newsletter";
import EventCarouse from "@/my_components/Events/EventCarouse";
import Brands from "../TrustedByBrands/Brands";

// LOAD DATA
// faqs.json now holds only structural metadata (category/question ids, icons, order) --
// the actual question/answer TEXT lives in public/locales/<lng>/faq.json so it can be
// translated per-language. The English copy of that namespace is imported here too, purely
// to serve as the `t()` fallback/default value for each key (in place of retyping the English
// text a second time in this file) -- see the `text()` helper below.
import SiteFaqData from "@/my_data/faq/faqs.json";
import enFaqFallback from "@/public/locales/en/faq.json";

// CONFIGS AND ROUTE
import { ROUTE } from "@/route/app_routes.js";
import { image_url } from "../../helper/Utilities";

const CATEGORIES = SiteFaqData.categories;

// Wrap every case-insensitive occurrence of `term` inside `text` in <mark>, so a search
// hit is visually obvious inside a long answer instead of the reader having to re-read it.
const highlight = (text, term) => {
  if (!term) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? <mark key={i}>{part}</mark> : part
  );
};

const KnowledgeBase = () => {
  const { t, i18n } = useTranslation("faq");
  // Arabic's question/answer text is genuinely translated (unlike blog/event content, which
  // stays in whatever language the author wrote it in and is deliberately never machine- or
  // hand-translated) -- so this is one of the few places a local dir="rtl" is actually correct
  // rather than the document-root approach that used to mirror the whole page's chrome. See
  // lib/i18n.js's isRtlLanguage() comment for the full reasoning.
  const contentDir = isRtlLanguage(i18n.language) ? "rtl" : undefined;
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(CATEGORIES[0]?.questions[0]?.id ?? null);
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]?.id ?? "");

  useEffect(() => {
    sal({
      threshold: 0.01,
      once: true,
    });
  }, []);

  // Looks up an English fallback value from the bundled faq.json by dot-path, e.g.
  // "categories.getting-started.questions.gs-is-free.question" -- used as the t() default
  // value below so this component doesn't have to duplicate all 34 Q&A strings inline; the
  // real, translated text is read from the "faq" i18next namespace at render time.
  const fallback = (path) =>
    path.split(".").reduce((node, key) => (node == null ? node : node[key]), enFaqFallback);

  const categoryTitle = (category) =>
    t(`categories.${category.id}.title`, fallback(`categories.${category.id}.title`) ?? category.id);

  const questionText = (categoryId, questionId) =>
    t(
      `categories.${categoryId}.questions.${questionId}.question`,
      fallback(`categories.${categoryId}.questions.${questionId}.question`) ?? questionId
    );

  const answerText = (categoryId, questionId) =>
    t(
      `categories.${categoryId}.questions.${questionId}.answer`,
      fallback(`categories.${categoryId}.questions.${questionId}.answer`) ?? ""
    );

  const normalizedQuery = query.trim().toLowerCase();

  // Filter categories/questions by the search box. When a search is active we also
  // force-expand every matching question, so the reader doesn't have to click through
  // a flat list of matched titles to see whether the answer is actually what they need.
  // Filters against the *translated* question/answer text, so search works correctly
  // against whichever language is currently active.
  const visibleCategories = useMemo(() => {
    if (!normalizedQuery) return CATEGORIES;
    return CATEGORIES.map((category) => ({
      ...category,
      questions: category.questions.filter((item) => {
        const q = questionText(category.id, item.id).toLowerCase();
        const a = answerText(category.id, item.id).toLowerCase();
        return q.includes(normalizedQuery) || a.includes(normalizedQuery);
      }),
    })).filter((category) => category.questions.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedQuery, t]);

  const totalMatches = useMemo(
    () => visibleCategories.reduce((sum, c) => sum + c.questions.length, 0),
    [visibleCategories]
  );

  // Scrollspy: highlight the jump-nav pill for whichever category section is currently
  // nearest the top of the viewport, so the nav stays in sync as the reader scrolls.
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;

    const sections = CATEGORIES.map((c) => document.getElementById(c.id)).filter(Boolean);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.id);
          }
        });
      },
      { rootMargin: "-160px 0px -70% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [visibleCategories.length]);

  const toggleQuestion = (id) => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <>
      <main className="rbt-main-wrapper">
        {/* ── Hero + Search ─────────────────────────────────────────────── */}
        <div className="rbt-page-hero">
          <div className="container">
            <span className="rbt-page-hero__eyebrow">{t("hero.eyebrow", "Help Center")}</span>
            <h1 className="rbt-page-hero__title">{t("hero.title", "How can we help you learn?")}</h1>
            <p className="rbt-page-hero__desc">
              {t(
                "hero.description",
                "Answers about enrolling in courses, earning certificates, joining events, and managing your account — all in one place."
              )}
            </p>

            <div className="rbt-faq-search">
              <i className="feather-search" aria-hidden="true"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("hero.searchPlaceholder", 'Search questions, e.g. "certificate" or "refund"')}
                aria-label={t("hero.searchAriaLabel", "Search frequently asked questions")}
              />
              {query && (
                <button
                  type="button"
                  className="rbt-faq-search__clear"
                  onClick={() => setQuery("")}
                  aria-label={t("hero.clearSearchAriaLabel", "Clear search")}
                >
                  <i className="feather-x" aria-hidden="true"></i>
                </button>
              )}
            </div>

            <ul className="page-list justify-content-center">
              <li className="rbt-breadcrumb-item">
                <Link href="/" style={{ color: "rgba(255,255,255,0.85)" }}>{t("breadcrumb.home", "Home")}</Link>
              </li>
              <li>
                <div className="icon-right">
                  <i className="feather-chevron-right" style={{ color: "rgba(255,255,255,0.65)" }}></i>
                </div>
              </li>
              <li className="rbt-breadcrumb-item active" style={{ color: "#fff" }}>{t("breadcrumb.current", "FAQ")}</li>
            </ul>
          </div>
        </div>

        {/* ── Category jump nav ─────────────────────────────────────────── */}
        {!normalizedQuery && (
          <div className="container">
            <nav className="rbt-page-jumpnav" aria-label="FAQ categories">
              {CATEGORIES.map((category) => (
                <a
                  key={category.id}
                  href={`#${category.id}`}
                  className={`rbt-page-jumpnav__pill ${activeCategory === category.id ? "is-active" : ""}`}
                >
                  <i className={category.icon} aria-hidden="true"></i>
                  {categoryTitle(category)}
                </a>
              ))}
            </nav>
          </div>
        )}

        {/* ── Q&A content ───────────────────────────────────────────────── */}
        <div className="rbt-accordion-area bg-color-white rbt-section-gapBottom">
          <div className="container">
            {normalizedQuery && (
              <p className="mb--30" style={{ color: "var(--color-body)" }}>
                {totalMatches > 0
                  ? t("search.resultsCount", {
                      count: totalMatches,
                      query,
                      defaultValue: `${totalMatches} result${totalMatches === 1 ? "" : "s"} for “${query}”`,
                    })
                  : t("search.noResults", {
                      query,
                      defaultValue: `No results for “${query}”`,
                    })}
              </p>
            )}

            {visibleCategories.length === 0 && (
              <div className="rbt-faq-empty">
                <div className="rbt-faq-empty__icon">
                  <i className="feather-search" aria-hidden="true"></i>
                </div>
                <h4>{t("empty.title", "We couldn't find a match")}</h4>
                <p>
                  {t(
                    "empty.description",
                    "Try a different keyword, or reach out to our support team directly and we'll answer your question personally."
                  )}
                </p>
                <Link href={ROUTE.contact ?? "/contact"} className="rbt-btn btn-gradient hover-icon-reverse radius-round">
                  <div className="icon-reverse-wrapper">
                    <span className="btn-text">{t("empty.cta", "Contact Support")}</span>
                    <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                    <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                  </div>
                </Link>
              </div>
            )}

            {visibleCategories.map((category) => (
              <section key={category.id} id={category.id} className="rbt-faq-category">
                <div className="rbt-faq-category__head">
                  <div className="rbt-faq-category__icon">
                    <i className={category.icon} aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3>{categoryTitle(category)}</h3>
                    <span className="rbt-faq-category__count">
                      {t("category.questionCount", {
                        count: category.questions.length,
                        defaultValue: `${category.questions.length} question${category.questions.length === 1 ? "" : "s"}`,
                      })}
                    </span>
                  </div>
                </div>

                <div className="rbt-faq-accordion">
                  {category.questions.map((item) => {
                    const isOpen = openId === item.id || Boolean(normalizedQuery);
                    return (
                      <div className={`rbt-faq-accordion__item ${isOpen ? "is-open" : ""}`} key={item.id}>
                        <button
                          type="button"
                          className="rbt-faq-accordion__trigger"
                          onClick={() => toggleQuestion(item.id)}
                          aria-expanded={isOpen}
                          aria-controls={`panel-${item.id}`}
                        >
                          <span dir={contentDir}>{highlight(questionText(category.id, item.id), query)}</span>
                          <span className="rbt-faq-accordion__icon" aria-hidden="true">
                            <i className="feather-plus"></i>
                          </span>
                        </button>
                        <div
                          id={`panel-${item.id}`}
                          className="rbt-faq-accordion__panel"
                          style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                        >
                          <div className="rbt-faq-accordion__panel-inner">
                            <p dir={contentDir}>{highlight(answerText(category.id, item.id), query)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>

        <div className="rbt-brand-area bg-color-white rbt-section-gap bg-gradient-11">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-10 offset-lg-1">
                <Brands />
              </div>
            </div>
          </div>
        </div>

        <div className="rbt-testimonial-area bg-color-white rbt-section-gap overflow-hidden">
          <div className="wrapper">
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <div className="section-title text-center">
                    <span className="subtitle bg-primary-opacity">
                      TESTIMONIALS
                    </span>
                    <h2 className="title"> What People Are Saying About Us! </h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Testimonial_2 />
        </div>

        <div className="rbt-event-area rbt-section-gap bg-gradient-3">
          <div className="container">
            <div className="row mb--55">
              <div className="section-title text-center">
                <span className="subtitle bg-white-opacity">
                  INTERESTED IN JOINING?
                </span>
                <h2 className="title color-white">Upcoming Events</h2>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                <EventCarouse />
              </div>
            </div>
          </div>
        </div>

        <div className="rbt-rbt-blog-area rbt-section-gap">
          <div className="container">
            <div className="row g-5 align-items-center mb--30">
              <div className="col-lg-6 col-md-6 col-12">
                <div className="section-title">
                  <span className="subtitle bg-primary-opacity">Blog Posts</span>
                  <h2 className="title">Never miss what we are up to</h2>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 col-12">
                <div className="read-more-btn text-start text-md-end">
                  <Link className="rbt-btn btn-gradient hover-icon-reverse" href={ROUTE.blog}>
                    <div className="icon-reverse-wrapper">
                      <span className="btn-text">See All Articles</span>
                      <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                      <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <BlogWidget />
          </div>
        </div>

        <CallToActionOne />

        <div className="rbt-newsletter-area bg-color-primary newsletter-style-2 rbt-section-gap"
          style={{ background: "url("+image_url('../app_images/bg/dots.png')+") repeat center/auto" }}>
          <Newsletter />
        </div>
      </main>
    </>
  );
};

export default KnowledgeBase;
